import { useMemo } from 'react';
import { Button } from '@cbhq/cds-web/buttons';
import { Icon } from '@cbhq/cds-web/icons';
import { Box, Grid, VStack } from '@cbhq/cds-web/layout';
import { TextBody, TextTitle3 } from '@cbhq/cds-web/typography';

import { useSimpleBreakpoints } from ':cloud/hooks/useSimpleBreakpoints';
import { STAKING_API_QUICKSTART } from ':cloud/utils/links';
import { openUrlInNewWindow } from ':cloud/utils/openUrlInNewWindow';
import { CodeBlock } from ':cloud/widgets/sharedcomponents';
import { CodeBlockCode } from ':cloud/widgets/sharedcomponents/CodeBlock/CodeBlock';

const codeExamples: CodeBlockCode[] = [
  {
    language: 'typescript',
    code: `let address = new ExternalAddress(Coinbase.networks.EthereumHolesky, "YOUR_WALLET_ADDRESS");
  
let stakeableBalance = await address.stakeableBalance(Coinbase.assets.Eth, StakeOptionsMode.NATIVE);

let stakingOperation = await address.buildStakeOperation(64, Coinbase.assets.Eth, StakeOptionsMode.NATIVE);

await stakingOperation.wait();`,
  },
  {
    language: 'go',
    code: `address := coinbase.NewExternalAddress(coinbase.EthereumHolesky, "YOUR_WALLET_ADDRESS")
  
stakeableBalance, err := client.GetStakeableBalance(
    ctx,
    coinbase.Eth,
    address,
    coinbase.WithStakingBalanceMode(coinbase.StakingOperationModeNative),
)

stakingOperation, err := client.BuildStakeOperation(
    ctx,
    big.NewFloat(32.00),
    coinbase.Eth,
    address,
    coinbase.WithStakingOperationMode(coinbase.StakingOperationModeNative),
)

stakingOperation, err = client.Wait(ctx, stakingOperation)`,
  },
];

const codeBlockStyle = {
  padding: '16px',
  background: '#000000',
  fontSize: '14px',
  width: '100%',
};

function CodeExampleSection() {
  const { isDesktop } = useSimpleBreakpoints();

  const responsiveTemplateColumns = useMemo(() => {
    return isDesktop ? '3fr 7fr' : '1fr';
  }, [isDesktop]);

  return (
    <Box borderedBottom borderedTop>
      <Grid templateColumns={responsiveTemplateColumns} gap={6} spacing={6}>
        <VStack gap={2} alignItems="baseline">
          <TextTitle3 as="h3">Seamless integration</TextTitle3>
          <TextBody as="p" color="foregroundMuted" spacingBottom={2}>
            Empower your users to earn rewards using our easy-to-integrate API and SDK. Our tools
            provide secure, scalable access to staking services, making it simple to offer rewards
            and earn initiatives directly within your platform.
          </TextBody>
          <Button
            onPress={() => openUrlInNewWindow(STAKING_API_QUICKSTART)}
            flush="start"
            transparent
            end={<Icon name="diagonalUpArrow" size="s" />}
          >
            View full quickstart
          </Button>
        </VStack>

        <Box>
          <CodeBlock
            codeArr={codeExamples}
            showLanguageSelect
            showCopy
            customStyle={codeBlockStyle}
            borderRadius="rounded"
            enableLineWrap
            showLineNumbers
            titleSpacingBottom={1}
            titleSpacingStart={0}
          />
        </Box>
      </Grid>
    </Box>
  );
}

export default CodeExampleSection;
